/**
 * Deterministic, theme-friendly colors for Category/Vertical and CMS chips.
 *
 * Each tag maps to a hue → a light pastel background + saturated dark text + subtle
 * border, so the same tag is always the same color across the app. Known tags get
 * curated hues so related concepts read coherently; unknown tags hash to a stable hue.
 */

export interface ChipColor {
  bg: string;
  fg: string;
  border: string;
}

// Curated hues (0–359) for the known Category/Vertical taxonomy.
const CATEGORY_HUE: Record<string, number> = {
  // Ownership / identity
  'diverse-owned': 200,
  'black-owned': 28,
  'woman-owned': 330,
  'aapi-owned': 158,
  'hispanic-owned': 14,
  'lgbt+': 305,
  'culture genesis-owned': 222,
  // Content verticals
  entertainment: 280,
  lifestyle: 188,
  comedy: 45,
  'sports & fitness': 130,
  education: 210,
  gaming: 262,
  footballco: 146,
  family: 36,
  automotive: 4,
  music: 318,
  beauty: 342,
  fashion: 292,
  'tech & innovation': 232,
  food: 22,
  'pop culture': 312,
  'travel & culture': 174,
  'business & finance': 152,
  politics: 350,
  animation: 250,
};

/** Stable string → hue in [0, 359). */
function hashHue(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h) % 360;
}

function hueToChip(h: number): ChipColor {
  return {
    bg: `hsl(${h} 72% 93%)`,
    fg: `hsl(${h} 60% 30%)`,
    border: `hsl(${h} 60% 82%)`,
  };
}

/** Color for a Category/Vertical tag. */
export function getCategoryColor(tag: string): ChipColor {
  const key = tag.trim().toLowerCase();
  const hue = key in CATEGORY_HUE ? CATEGORY_HUE[key] : hashHue(key);
  return hueToChip(hue);
}

// CMS chips — fixed palette per CmsSource slug (mirrors backend CMS_SLUG_LABEL keys).
const CMS_HUE: Record<string, number> = {
  'culture-genesis': 222,
  'cg-affiliate-2': 150,
  'cg-3': 270,
  'all-def': 24,
  'cg-4': 330,
  unknown: 0,
};

/** Color for a CMS, keyed by its CmsSource slug (not a display label). */
export function getCmsColor(cmsSlug: string): ChipColor {
  const key = cmsSlug.trim().toLowerCase();
  if (key === 'unknown' || !key) return { bg: 'var(--bg-surface-2)', fg: 'var(--fg-3)', border: 'var(--border-1)' };
  const hue = key in CMS_HUE ? CMS_HUE[key] : hashHue(key);
  return hueToChip(hue);
}

/** Saturated fill color (with white text) for an initial-avatar fallback, seeded by a string. */
export function getAvatarColor(seed: string): string {
  return `hsl(${hashHue(seed.trim().toLowerCase())} 52% 52%)`;
}
